import React, { useState } from "react";
import { motion } from "framer-motion";
import { Award, Building2, Mail, Phone, User, MessageSquare, CheckCircle2, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const TIERS = [
  { v: "or", label: "Or", desc: "Visibilité maximale · Mention officielle · Logo XL" },
  { v: "argent", label: "Argent", desc: "Logo grand format · Mention sur tous les supports" },
  { v: "bronze", label: "Bronze", desc: "Logo dans la section partenaires" },
  { v: "custom", label: "Sur mesure", desc: "Discutons d'une formule adaptée à vos besoins" },
];

const DevenirCommanditaire = () => {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    desired_tier: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/contact/sponsor", form);
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="devenir-commanditaire-page">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
            Rejoignez l'aventure
          </span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.95]">
            Devenir <span className="text-[#E60012]">commanditaire</span>
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            La Coupe CH rassemble plus de 200 jeunes joueurs, leurs familles et leurs entraîneurs
            pendant 3 jours. Associez votre entreprise à un événement positif, énergique et fédérateur.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
        {/* Tiers */}
        <div className="lg:col-span-5 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Award className="w-5 h-5 text-[#E60012]" />
            <h2 className="font-display text-3xl uppercase">Nos formules</h2>
          </div>
          {TIERS.map((t, i) => (
            <motion.button
              key={t.v}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              onClick={() => setF("desired_tier", t.v)}
              data-testid={`tier-option-${t.v}`}
              className={`w-full text-left p-5 border transition-all hover:-translate-y-0.5 ${
                form.desired_tier === t.v
                  ? "bg-[#E60012]/10 border-[#E60012]"
                  : "bg-[#111] border-white/10 hover:border-[#E60012]/60"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-display text-2xl uppercase">{t.label}</div>
                {form.desired_tier === t.v && <CheckCircle2 className="w-5 h-5 text-[#E60012]" />}
              </div>
              <p className="mt-1 text-white/65 text-sm">{t.desc}</p>
            </motion.button>
          ))}

          <div className="mt-6 bg-gradient-to-br from-[#1a0306] to-[#0a0a0a] border border-[#E60012]/30 p-6">
            <h3 className="font-display text-xl uppercase">Pourquoi nous ?</h3>
            <ul className="mt-3 space-y-2 text-sm text-white/75">
              <li className="flex items-start gap-2"><span className="text-[#E60012]">▸</span> 200+ jeunes joueurs et leurs familles</li>
              <li className="flex items-start gap-2"><span className="text-[#E60012]">▸</span> 29 parties · 3 jours d'achalandage</li>
              <li className="flex items-start gap-2"><span className="text-[#E60012]">▸</span> Visibilité numérique sur tous les supports</li>
              <li className="flex items-start gap-2"><span className="text-[#E60012]">▸</span> Image positive associée à la relève</li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          {success ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#111] border border-[#E60012]/30 p-10 text-center"
              data-testid="contact-success"
            >
              <CheckCircle2 className="w-14 h-14 text-[#E60012] mx-auto mb-4" />
              <h3 className="font-display text-3xl uppercase">Merci !</h3>
              <p className="mt-3 text-white/70">
                Votre demande a bien été envoyée. Notre équipe vous contactera sous peu pour
                discuter d'un partenariat sur mesure.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="bg-[#111] border border-white/10 p-6 sm:p-8 clip-jersey" data-testid="contact-form">
              <div className="flex items-center gap-3 mb-5">
                <Mail className="w-5 h-5 text-[#E60012]" />
                <h2 className="font-display text-3xl uppercase">Demande de partenariat</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field icon={Building2} label="Entreprise *">
                  <input
                    required
                    type="text"
                    value={form.company_name}
                    onChange={(e) => setF("company_name", e.target.value)}
                    data-testid="company-input"
                    className="w-full bg-black border border-white/10 px-3 py-2.5 outline-none focus:border-[#E60012]"
                    placeholder="Nom de votre entreprise"
                  />
                </Field>
                <Field icon={User} label="Personne contact *">
                  <input
                    required
                    type="text"
                    value={form.contact_name}
                    onChange={(e) => setF("contact_name", e.target.value)}
                    data-testid="name-input"
                    className="w-full bg-black border border-white/10 px-3 py-2.5 outline-none focus:border-[#E60012]"
                    placeholder="Prénom Nom"
                  />
                </Field>
                <Field icon={Mail} label="Courriel *">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setF("email", e.target.value)}
                    data-testid="email-input"
                    className="w-full bg-black border border-white/10 px-3 py-2.5 outline-none focus:border-[#E60012]"
                    placeholder="vous@entreprise.com"
                  />
                </Field>
                <Field icon={Phone} label="Téléphone">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setF("phone", e.target.value)}
                    data-testid="phone-input"
                    className="w-full bg-black border border-white/10 px-3 py-2.5 outline-none focus:border-[#E60012]"
                    placeholder="Votre téléphone"
                  />
                </Field>
              </div>

              <div className="mt-4">
                <span className="font-condensed uppercase tracking-[0.2em] text-xs text-white/60 block mb-2">
                  Niveau souhaité (optionnel)
                </span>
                <div className="flex gap-2 flex-wrap">
                  {TIERS.map((t) => (
                    <button
                      type="button"
                      key={t.v}
                      data-testid={`form-tier-${t.v}`}
                      onClick={() => setF("desired_tier", t.v)}
                      className={`px-3 py-1.5 text-xs font-condensed uppercase tracking-[0.15em] border ${
                        form.desired_tier === t.v
                          ? "bg-[#E60012] text-white border-[#E60012]"
                          : "border-white/15 text-white/70 hover:border-[#E60012]"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <Field icon={MessageSquare} label="Message">
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => setF("message", e.target.value)}
                    data-testid="message-input"
                    className="w-full bg-black border border-white/10 px-3 py-2.5 outline-none focus:border-[#E60012] resize-y"
                    placeholder="Parlez-nous de votre entreprise et de vos attentes..."
                  />
                </Field>
              </div>

              {error && <div className="mt-4 text-sm text-red-400">{error}</div>}

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-btn"
                className="mt-6 inline-flex items-center gap-3 px-7 py-3.5 bg-[#E60012] hover:bg-[#ff1a30] disabled:opacity-50 text-white font-condensed uppercase tracking-[0.2em] text-sm transition-all hover:-translate-y-0.5 clip-tab"
              >
                {submitting ? "Envoi…" : "Envoyer la demande"}
                <ArrowRight className="w-4 h-4" />
              </button>

              <p className="mt-3 text-xs text-white/50">
                Vos informations restent confidentielles et ne seront utilisées que pour vous recontacter.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

const Field = ({ icon: Icon, label, children }) => (
  <label className="block">
    <span className="font-condensed uppercase tracking-[0.2em] text-xs text-white/60 flex items-center gap-1.5 mb-1">
      <Icon className="w-3.5 h-3.5 text-[#E60012]" /> {label}
    </span>
    {children}
  </label>
);

export default DevenirCommanditaire;
